// O que o relatório HTML pode pedir ao portal para abrir (Portal.abrir): WhatsApp, telefone e sites que o BI liberou.
//
// O relatório nunca manda um endereço pronto para o portal abrir "no escuro": manda o tipo e os dados (telefone, mensagem, ou o site), e é aqui
// que o endereço é montado e conferido. WhatsApp e telefone têm endereço próprio, montado a partir do número. Qualquer site https pode ser
// aberto, mas o portal sempre pergunta antes, mostrando o endereço inteiro (um relatório escrito por terceiros poderia levar dados na URL, e a
// pergunta é o que deixa isso à vista). Só os sites da lista do BI (contexto.linksLiberados) abrem sem perguntar.

export type TipoDeAbertura = 'whatsapp' | 'telefone' | 'link'

export const MAX_MENSAGEM = 1000
export const MAX_ENDERECO = 2000

export interface Alvo {
  tipo: TipoDeAbertura
  /** O endereço que o portal abre (https://wa.me/..., tel:+55... ou o site). */
  url: string
  /** A frase da pergunta que o portal faz quando o navegador não deixa abrir sozinho. */
  titulo: string
  detalhe: string
  /** Só no WhatsApp: o texto que vai pronto na conversa. */
  mensagem?: string
  botao: string
  /** O portal pergunta antes de abrir, mesmo com toque recente: ligação e site que não está na lista do BI. */
  confirmar: boolean
  /** O que vai ao log de ações: o tipo e um destino curto (4 últimos dígitos, ou o site). Nunca a mensagem. */
  registro: { tipo: TipoDeAbertura; destino: string }
}

/** Dígitos do Brasil com o 55 na frente (DDD + 8 ou 9 dígitos), ou null. O DDD 55 (Santa Maria) com o número sem o 55 também vale. */
export function normalizarTelefone(valor: unknown): string | null {
  const texto = String(valor ?? '').trim()
  let d = texto.replace(/\D/g, '')
  // "+1 415 555 0100" e "001 415..." dizem que o número é de outro país: sem o 55 na frente, não é nosso e não ganha DDD.
  if (texto.startsWith('+') || d.startsWith('00')) {
    if (d.startsWith('00')) d = d.slice(2)
    if (!d.startsWith('55')) return null
  }
  if (d.length === 10 || d.length === 11) d = `55${d}`
  return /^55[1-9]\d\d{8,9}$/.test(d) ? d : null
}

/** "+55 (11) 98888-7777". */
export function formatarTelefone(d55: string): string {
  const ddd = d55.slice(2, 4)
  const resto = d55.slice(4)
  const corte = resto.length - 4
  return `+55 (${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`
}

const ultimos4 = (d55: string) => `…${d55.slice(-4)}`

/** Está na lista do BI (abre sem perguntar)? Compara o endereço exato: subdomínio não vale por causa do domínio. */
export function hostLiberado(host: string, lista: string[]): boolean {
  const h = host.trim().toLowerCase()
  return h.length > 0 && lista.some(x => x.trim().toLowerCase() === h)
}

/** Um endereço de site de verdade: com ponto, sem IP e sem nome interno ("localhost", "servidor"). */
export function hostDeSite(host: string): boolean {
  const h = host.trim().toLowerCase()
  if (!h.includes('.') || h.endsWith('.') || h.includes(':')) return false
  return !/^\d{1,3}(\.\d{1,3}){3}$/.test(h)
}

/**
 * Confere o pedido do relatório e monta o que o portal vai abrir. Lança Error com a frase que o relatório mostra à pessoa
 * (volta como erro.message na Promise do Portal.abrir).
 */
export function prepararAbertura(destino: unknown, linksLiberados: string[]): Alvo {
  const d = (destino && typeof destino === 'object' ? destino : {}) as Record<string, unknown>
  const tipo = d.tipo

  if (tipo === 'whatsapp' || tipo === 'telefone') {
    const numero = normalizarTelefone(d.telefone)
    if (!numero) throw new Error('Telefone inválido')
    const formatado = formatarTelefone(numero)
    if (tipo === 'telefone') {
      return {
        tipo, url: `tel:+${numero}`, titulo: `Ligar para ${formatado}?`, detalhe: 'O portal vai abrir o aplicativo de telefone.',
        botao: 'Ligar', confirmar: true, registro: { tipo, destino: ultimos4(numero) }
      }
    }
    const mensagem = typeof d.mensagem === 'string' ? d.mensagem.trim().slice(0, MAX_MENSAGEM) : ''
    return {
      tipo, url: `https://wa.me/${numero}${mensagem ? `?text=${encodeURIComponent(mensagem)}` : ''}`,
      titulo: `Abrir conversa no WhatsApp com ${formatado}?`,
      detalhe: mensagem ? 'A conversa abre com esta mensagem pronta. Você só envia.' : 'A conversa abre sem mensagem pronta.',
      mensagem: mensagem || undefined, botao: 'Abrir WhatsApp', confirmar: false, registro: { tipo, destino: ultimos4(numero) }
    }
  }

  if (tipo === 'link') {
    const bruto = typeof d.url === 'string' ? d.url.trim() : ''
    if (!bruto || bruto.length > MAX_ENDERECO) throw new Error('Endereço inválido')
    let u: URL
    try {
      u = new URL(bruto)
    } catch {
      throw new Error('Endereço inválido')
    }
    if (u.protocol !== 'https:') throw new Error('Só endereços https são abertos')
    // Usuário e senha na frente do endereço servem para disfarçar o destino ("https://www.exemplo.com@outro.com"), e porta diferente é de sistema interno.
    if (u.username || u.password || (u.port && u.port !== '443')) throw new Error('Endereço inválido')
    if (!hostDeSite(u.hostname)) throw new Error('Endereço inválido')
    return {
      tipo, url: u.href, titulo: `Abrir ${u.hostname}?`, detalhe: u.href.length > 300 ? `${u.href.slice(0, 297)}…` : u.href,
      botao: 'Abrir site', confirmar: !hostLiberado(u.hostname, linksLiberados), registro: { tipo, destino: u.hostname }
    }
  }

  throw new Error('Tipo de abertura desconhecido')
}
